/**
 * What the modal and the drawer share: leaving the screen without eating a
 * click, knowing which of several open overlays Escape belongs to, and where
 * focus lands when one opens.
 *
 * THE CLICK AFTER ESCAPE. Salesforge closes a modal on Escape and leaves its
 * scrim in place for the length of the exit animation, so the next click lands
 * on the scrim instead of the button under the pointer. A person clicks a real
 * control, nothing happens, and they click again. Here the phase turns to
 * `closing` in the same render that `open` turns false, and a closing overlay
 * renders with `pointer-events: none` on its root. React commits a keydown
 * synchronously, so the DOM has stopped taking input before the next click can
 * arrive. The exit animation then plays on a node that only draws.
 *
 * WHY ESCAPE LISTENS ON THE WINDOW AND NOT THE DOCUMENT. A select or a menu
 * open inside a modal closes itself on Escape through `useOnEscape`, which
 * listens on the document and stops propagation. Listening one level further
 * out, on the window, means their stop actually stops this, so one press closes
 * the select and leaves the modal standing. Two overlays open at once (a
 * confirm over a drawer) are told apart by the layer stack below: only the
 * newest one answers.
 *
 * The rules are plain functions, because this workspace has no DOM test
 * environment and a rule inside a hook is a rule nobody can cover.
 */
import { useEffect, useRef, useState } from 'react'

// ---------------------------------------------------------------------------
// Presence
// ---------------------------------------------------------------------------

export type OverlayPhase = 'open' | 'closing' | 'closed'

/**
 * The phase to draw, from the `open` prop and the phase last settled.
 *
 * `closing` is derived rather than stored so it holds from the very render in
 * which `open` went false. Waiting for an effect to store it would leave one
 * committed frame where the overlay is still `open` and still takes clicks,
 * which is the whole defect.
 */
export function resolvePhase(open: boolean, settled: OverlayPhase): OverlayPhase {
  if (open) return 'open'
  return settled === 'closed' ? 'closed' : 'closing'
}

/** Whether an overlay in this phase may take pointer input or hold focus. */
export function overlayTakesInput(phase: OverlayPhase): boolean {
  return phase === 'open'
}

/** Reduced motion skips the exit, so a node that only draws is gone at once. */
export function exitDuration(exitMs: number, reducedMotion: boolean): number {
  return reducedMotion ? 0 : Math.max(0, exitMs)
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Keeps an overlay mounted for `exitMs` after it was told to close, so its exit
 * can play, and reports which phase to draw.
 */
export function useOverlayPresence(open: boolean, exitMs: number): OverlayPhase {
  const [settled, setSettled] = useState<OverlayPhase>(open ? 'open' : 'closed')
  const phase = resolvePhase(open, settled)

  useEffect(() => {
    if (open) {
      setSettled('open')
      return undefined
    }
    if (settled === 'closed') return undefined
    const timer = window.setTimeout(() => setSettled('closed'), exitDuration(exitMs, prefersReducedMotion()))
    return () => window.clearTimeout(timer)
  }, [open, settled, exitMs])

  return phase
}

// ---------------------------------------------------------------------------
// The layer stack, so Escape closes one thing at a time
// ---------------------------------------------------------------------------

/**
 * Layer ids in the order they opened. Module state on purpose: two overlays in
 * unrelated parts of the tree have no common parent to hold this for them.
 */
const layers: number[] = []
let nextLayerId = 1

export function pushLayer(): number {
  const id = nextLayerId
  nextLayerId += 1
  layers.push(id)
  return id
}

export function removeLayer(id: number): void {
  const at = layers.indexOf(id)
  if (at !== -1) layers.splice(at, 1)
}

export function isTopLayer(id: number): boolean {
  return layers.length > 0 && layers[layers.length - 1] === id
}

/** For tests only: the stack as it stands. */
export function openLayers(): readonly number[] {
  return [...layers]
}

/**
 * Escape for an overlay, answered only by the newest open one.
 *
 * The listener is on the window, after the document has had its turn (see the
 * header for why). `defaultPrevented` is honoured too, so a field that spends
 * Escape on clearing itself can keep it.
 */
export function useOverlayEscape(active: boolean, handler: () => void): void {
  const saved = useRef(handler)
  saved.current = handler
  useEffect(() => {
    if (!active) return undefined
    const layer = pushLayer()
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape' || event.defaultPrevented) return
      if (!isTopLayer(layer)) return
      event.preventDefault()
      saved.current()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      removeLayer(layer)
    }
  }, [active])
}

// ---------------------------------------------------------------------------
// Where focus lands when an overlay opens
// ---------------------------------------------------------------------------

/** The part of `Element` this needs, so a test can hand it a plain object. */
export interface FocusRoot {
  querySelector(selector: string): unknown
}

/** A field a person types into or picks from, in the order they appear. */
export const FIELD_SELECTOR =
  'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [role="combobox"]:not([aria-disabled="true"]), [role="switch"]:not([disabled])'

/** The footer's primary, which a footer marks with `data-primary`. */
export const PRIMARY_SELECTOR = '[data-overlay-footer] [data-primary]:not([disabled])'

/**
 * Where focus goes when an overlay opens: the first field in its body, or the
 * primary when it has no fields (a button marked `data-primary`, else the last
 * button in the footer, which is where a primary sits), or failing all of
 * those nothing, and the caller then focuses the panel itself.
 *
 * The close X is never the answer, although it is the first focusable thing in
 * the markup. Landing on it means the first keypress somebody makes in a form
 * is a Tab past a button that would throw the form away.
 */
export function pickInitialFocus<T>(root: FocusRoot): T | null {
  const body = root.querySelector('[data-overlay-body]') as FocusRoot | null
  const field = body?.querySelector(FIELD_SELECTOR)
  if (field) return field as T
  const primary = root.querySelector(PRIMARY_SELECTOR)
  if (primary) return primary as T
  const footer = root.querySelector('[data-overlay-footer]') as
    | (FocusRoot & { querySelectorAll?: (selector: string) => ArrayLike<unknown> })
    | null
  const buttons = footer?.querySelectorAll?.('button:not([disabled])')
  if (buttons && buttons.length > 0) return buttons[buttons.length - 1] as T
  return null
}

/**
 * Moves focus to `pickInitialFocus` once the overlay is open. It runs after the
 * shared focus trap, which has already put focus on the first focusable thing,
 * and corrects it.
 */
export function useInitialFocus(active: boolean, containerRef: { current: HTMLElement | null }): void {
  useEffect(() => {
    if (!active) return
    const container = containerRef.current
    if (!container) return
    const target = pickInitialFocus<HTMLElement>(container)
    ;(target ?? container).focus({ preventScroll: true })
  }, [active, containerRef])
}
